import { useConfiguratorStateDomain } from "@/configurator/domain/hooks/useConfiguratorStateDomain";
import {
  buildARShareUrl,
  parseARShareParams,
} from "@/configurator/domain/logic/arShareLink";
import type { ARShareLinkParams } from "@/configurator/domain/logic/arShareLink";
import { buildSharedSelection } from "@/configurator/domain/logic/sharing";
import type { SharedSelection } from "@/configurator/domain/models/types";
import type { ConfiguratorSnapshot } from "@/configurator/domain/ports/configuratorState";

// Module scope keeps the identity stable — see the note in useConfiguratorDomain.
const selectSharedSelection = (s: ConfiguratorSnapshot) =>
  buildSharedSelection(
    s.config,
    s.activeProductId,
    s.selectedModelId,
    s.selectedOptions,
  );

export type ArShareDomain = {
  /** The link a phone should open to continue this configuration in AR. */
  buildShareUrl: (pageUrl: string) => string;
  /** Reads a configuration back out of a `location.search` string. */
  readShareLink: (search: string) => ARShareLinkParams;
  /** Puts a configuration that arrived on such a link into the store. */
  applySharedSelection: (selection: SharedSelection) => void;
};

/**
 * The desktop-to-phone hand-off. Both halves live together because they are one
 * contract: the parameters written into the link and the validation that reads
 * them back are the widget's public API (see PROJECT-RULES.md).
 */
export function useArShareDomain(): ArShareDomain {
  const { useSelect, commands } = useConfiguratorStateDomain();
  const selection = useSelect(selectSharedSelection);

  return {
    buildShareUrl: (pageUrl) => buildARShareUrl(pageUrl, selection),
    readShareLink: parseARShareParams,
    applySharedSelection: commands.applySharedSelection,
  };
}
